import type {
  EmailIntegration,
  UpdateEmailIntegrationApiArg,
  UpdateEmailIntegrationPatchField,
} from 'lib-shared-types-api';
import { useCallback } from 'react';

import { integrationsApi } from 'apis/integrations';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { sanitizeHtml } from 'utils/sanitizeHtml';

import { getTrimmedSignatureTemplate } from './helper';

type CallbackPayload = {
  emailAddress: EmailIntegration['email_address'];
  senderName: EmailIntegration['sender_name'];
  signatureTemplate: EmailIntegration['signature_template'];
};

/** A hook that handles updating an email. */
export const useUpdateEmail = () => {
  const dispatch = useAppDispatch();

  const updateEmail = useCallback(
    ({ emailAddress, senderName, signatureTemplate }: CallbackPayload) => {
      const updates = {
        ...(senderName && {
          sender_name: senderName?.trim() || null,
        }),
        ...(signatureTemplate && {
          signature_template: signatureTemplate
            ? sanitizeHtml(
                getTrimmedSignatureTemplate(signatureTemplate) ?? '',
                'email',
              )
            : null,
        }),
      };
      const deletes: UpdateEmailIntegrationPatchField[] = [];

      if (!senderName) {
        deletes.push('sender_name');
      }

      if (!signatureTemplate) {
        deletes.push('signature_template');
      }

      const payload: UpdateEmailIntegrationApiArg = {
        emailAddress,
        patch: { updates, deletes },
      };

      return dispatch(
        integrationsApi.endpoints.updateEmailIntegration.initiate(payload),
      );
    },
    [dispatch],
  );

  return updateEmail;
};
